import { useState } from "react";
import FadeUp from "./FadeUp";
import { Plus, Minus } from "lucide-react";

const faqs = [
    {
        q: "What makes you different from an influencer agency?",
        a: "Most influencer agencies are built around campaigns. I build systems. The goal isn't to run a campaign and disappear. It's to create a repeatable acquisition channel your team can keep running after I'm gone.",
    },
    {
        q: "Do you only work with YouTube creators?",
        a: "No. YouTube is one channel we use. Depending on your product and audience, we work across YouTube, newsletters, podcasts, LinkedIn, Instagram and TikTok. The channel is picked for where your buyers already spend time.",
    },
    {
        q: "Do you guarantee sales?",
        a: "No. Creator marketing still has variables we can't control. What we do guarantee is that we build the campaign around measurable business outcomes rather than vanity metrics, and we structure deals so the economics make sense even in a flat scenario.",
    },
    {
        q: "What kinds of apps do you work with?",
        a: "Any app whose core value is saving people time, improving work, or being powered by AI. That includes AI productivity assistants, note takers, calendars, meeting tools, AI writing and research, knowledge management, workflow automation, and most B2B SaaS where the product can be demonstrated through creator content.",
    },
    {
        q: "Do we actually own everything?",
        a: "Yes. Creator database, SOPs, tracking, tooling access, and trained team members all transfer to you at the end. Nothing walks out with me.",
    },
];

export const FAQ = () => {
    const [open, setOpen] = useState(0);

    return (
        <section
            id="faq"
            data-testid="faq-section"
            className="py-24 md:py-32 bg-white"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-14 lg:gap-20 items-start">
                    <div className="lg:sticky lg:top-32">
                        <FadeUp>
                            <p className="text-xs font-bold tracking-[0.22em] uppercase text-gray-500">
                                FAQ
                            </p>
                        </FadeUp>
                        <FadeUp delay={0.05}>
                            <h2
                                data-testid="faq-heading"
                                className="mt-4 font-display font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.3] text-[#0A0A0A] text-balance"
                            >
                                Questions founders ask.
                            </h2>
                        </FadeUp>
                        <FadeUp delay={0.1}>
                            <p className="mt-5 text-[15.5px] md:text-base text-gray-600 leading-relaxed max-w-md">
                                If yours isn't here, ask on the call. I answer everything directly, including what I won't do.
                            </p>
                        </FadeUp>
                    </div>

                    <div className="divide-y divide-black/[0.08] border-y border-black/[0.08]">
                        {faqs.map((f, i) => {
                            const isOpen = open === i;
                            return (
                                <div key={i} data-testid={`faq-item-${i}`}>
                                    <button
                                        onClick={() => setOpen(isOpen ? -1 : i)}
                                        aria-expanded={isOpen}
                                        data-testid={`faq-toggle-${i}`}
                                        className="w-full flex items-start justify-between gap-6 py-6 md:py-7 text-left group"
                                    >
                                        <span className="font-display font-bold text-[17px] md:text-lg tracking-tight text-[#0A0A0A] leading-[1.3]">
                                            {f.q}
                                        </span>
                                        <span
                                            className={`flex-none h-9 w-9 rounded-full border border-black/[0.08] flex items-center justify-center transition-colors ${
                                                isOpen
                                                    ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                                                    : "bg-white text-[#0A0A0A] group-hover:border-black/30"
                                            }`}
                                        >
                                            {isOpen ? (
                                                <Minus className="h-4 w-4" strokeWidth={2.5} />
                                            ) : (
                                                <Plus className="h-4 w-4" strokeWidth={2.5} />
                                            )}
                                        </span>
                                    </button>
                                    <div
                                        className={`overflow-hidden transition-all duration-300 ${
                                            isOpen ? "max-h-[600px] pb-6 md:pb-7" : "max-h-0"
                                        }`}
                                    >
                                        <p className="text-[15px] text-gray-600 leading-relaxed max-w-2xl">
                                            {f.a}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FAQ;
